import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  AppConfigProvider,
  Auth0ProviderWithNavigate,
  AuthenticationGuard,
  EnvConfigProvider,
} from "rwo-react";
import { App } from "./App";
import "./assets/css/App.css";
import { LoadingPage } from "./components/LoadingPage";
import { PostLogoutHandler } from "./components/PostLogoutHandler";
import { initializeI18n } from "./localization";
import { TelemetryInitializer } from "./telemetry/TelemetryInitializer";
import { theme } from "./theme";

initializeI18n();

const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") return undefined;

  const err = error as Record<string, unknown>;

  if (typeof err.status === "number" && err.status !== 0) {
    return err.status;
  }

  if (typeof err.statusCode === "number" && err.statusCode !== 0) {
    return err.statusCode;
  }

  if (err.cause) {
    return getHttpStatus(err.cause);
  }

  return undefined;
};

const client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: unknown) => {
        const status = getHttpStatus(error);

        // Disable retries on 401 Unauthorized or 403 Forbidden
        if (status === 403) {
          return false;
        }

        return failureCount < 1;
      },
    },
  },
});

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <EnvConfigProvider
        config={process.env as Record<string, string>}
        devUri="http://localhost:3000"
      >
        <QueryClientProvider client={client}>
          <BrowserRouter>
            <Auth0ProviderWithNavigate cacheType="sessionstorage">
              <PostLogoutHandler loadingPage={<LoadingPage />}>
                <AuthenticationGuard loadingPage={<LoadingPage />}>
                  <AppConfigProvider loadingFallback={<LoadingPage />}>
                    <TelemetryInitializer />
                    <App />
                  </AppConfigProvider>
                </AuthenticationGuard>
              </PostLogoutHandler>
            </Auth0ProviderWithNavigate>
          </BrowserRouter>
        </QueryClientProvider>
      </EnvConfigProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
